<template>
  <div class="bootcamp-modal" role="alert">
    <div class="bootcamp-modal__wrapper">
      <button
        @click="$emit('close-bootcamp-modal')"
        :class="[
          'bootcamp-modal__close',
          isLoading && 'bootcamp-modal__close--disabled',
        ]"
        :disabled="isLoading"
      >
        <CloseIcon class="bootcamp-modal__close--icon" />
      </button>
      <div class="bootcamp-modal__content">
        <div class="bootcamp-modal__heading">
          <h6 class="bootcamp-modal__tagline">Almost Done!</h6>
          <p class="bootcamp-modal__info">
            Check the information below and confirm your Bootcamp registration.
            You will receive notifications and instructions from Zoom via email.
          </p>
        </div>
        <div class="bootcamp-modal__details-wrapper">
          <div class="bootcamp-modal__title-wrapper">
            <Passes
              :passName="learningAreaName"
              :size="45"
              class="bootcamp-modal__pass"
            />
            <h4 class="bootcamp-modal__title">{{ bootcamp.name }}</h4>
          </div>
          <div class="bootcamp-modal__details">
            <p class="bootcamp-modal__detail">
              <ProfileIcon class="bootcamp-modal__detail--icon" />
              {{ instructorName }}
            </p>
            <p class="bootcamp-modal__detail">
              <CalendarIcon class="bootcamp-modal__detail--icon" />
              {{
                formatDate({
                  start: bootcamp.start_datetime,
                  end: bootcamp.end_datetime,
                })
              }}
              <i
                class="bootcamp__timezone-icon material-icons info-icon"
                role="presentation"
                v-tooltip="{
                  content: `Local time: ${localTimeZone}`,
                  placement: 'top-center',
                }"
              >
                info_outline
              </i>
            </p>
          </div>
        </div>
        <div class="bootcamp-modal__bottom">
          <p class="bootcamp-modal__bottom--info">Student: {{ fullName }}</p>
          <p class="bootcamp-modal__bottom--info">Email: {{ email }}</p>
          <div class="bootcamp-modal__bottom--actions">
            <ActionLoader v-if="isLoading" />
            <Action
              v-else
              @click="handleClick(bootcamp.id)"
              ofType="large"
              content="Confirm"
              :customClass="'bootcamp-modal__register-btn'"
              :disabled="isLoading"
              :isLoading="isLoading"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Passes from '@/components/properties/Passes.vue'
import Action from '@/components/shared/Action.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import ProfileIcon from '@/assets/icons/bootcamps/instructor_icon.svg'
import CalendarIcon from '@/assets/icons/bootcamps/date_icon.svg'
import CloseIcon from '@/assets/icons/navigation/close_icon.svg'
import { localTimezone } from '@/helpers/mixins'

export default {
  name: 'BootcampConfirmationModal',
  mixins: [localTimezone],
  components: {
    Passes,
    Action,
    ActionLoader,
    ProfileIcon: { ...ProfileIcon, name: 'ProfileIcon' },
    CloseIcon: { ...CloseIcon, name: 'CloseIcon' },
    CalendarIcon: { ...CalendarIcon, name: 'CalendarIcon' },
  },
  props: {
    bootcamp: {
      type: Object,
      required: true,
    },
    learningAreaName: {
      type: String,
      required: true,
    },
    profile: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    formatDate: {
      type: Function,
      required: true,
    },
  },
  computed: {
    instructorName() {
      const fallback = 'INE Instructor'
      const {
        bootcamp: { instructor },
      } = this
      return instructor && instructor.name ? instructor.name : fallback
    },
    email() {
      const {
        profile: { email },
      } = this
      return email
    },
    fullName() {
      const {
        profile: { first_name, last_name },
      } = this
      return `${first_name} ${last_name}`
    },
  },
  methods: {
    handleClick(id) {
      this.$emit('confirm-btn-clicked', id)
    },
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
}
</script>

<style lang="scss">
.bootcamp-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.75);
  transition: opacity 0.3s ease;
  &__wrapper {
    background-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.5),
        rgba(0, 0, 0, 0.5)
      ),
      url('../../assets/images/subscriptions/ccupdate.png');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: right;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 0px auto;
    background-color: var(--light-gray);
    color: var(--white);
    font-size: rem(16px);
    padding: 20px 0;
    top: 0;
    height: 100%;
    border-radius: 5px;
    padding: 50px 20px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    font-family: var(--font-bold);
    @include desktop {
      top: 5%;
      width: 90%;
      max-width: 1600px;
      height: 90%;
      padding: 20px 150px;
      box-shadow: 5px 5px 50px 5px rgba(255, 255, 255, 0.3);
    }
  }
  &__close {
    position: absolute;
    right: 20px;
    top: 10px;
    background: none;
    border: none;
    cursor: pointer;
    color: white;
    &:hover {
      .bootcamp-modal__close--icon {
        fill: var(--orange);
      }
    }
    &--icon {
      fill: var(--white);
    }
    &--disabled {
      pointer-events: none;
    }
  }
  &__heading {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    text-align: center;
    margin: 0 0 auto 0;
  }
  &__tagline {
    font-size: rem(27px);
    color: var(--orange);
    margin: 0;
    padding: 0;
    width: 100%;
  }
  &__info {
    color: var(--white);
    font-size: rem(18px);
    width: 100%;
    max-width: 669px;
  }
  &__details-wrapper {
    width: 100%;
    background: var(--dark-gray);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 50px 0 30px 0;
    flex-wrap: wrap;
  }
  &__title-wrapper {
    width: 100%;
    padding: 25px 50px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__pass {
    margin: 4px 25px 0 0;
    @include tablet {
      margin: 4px 0 0 0;
    }
  }
  &__title {
    padding: 0;
    margin: 0 0 0 auto;
    @include tablet {
      margin: 0 0 0 50px;
    }
  }
  &__details {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    background: var(--light-gray);
    padding: 15px 0;
  }
  &__detail {
    padding: 0;
    margin: 0;
    display: flex;
    &:nth-of-type(1) {
      margin: 0 50px 0 0;
    }
    &--icon {
      margin: 0 5px 0 0;
    }
  }
  &__bottom {
    margin: 10% 0 0 0;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    &--info {
      margin: 0;
      padding: 0;
      font-size: rem(18px);
      &:nth-of-type(1) {
        margin: 0 50px 0 0;
      }
    }
    &--actions {
      width: 100%;
      margin: 10% 0 0 0;
      display: flex;
      justify-content: center;
    }
  }

  &__register-btn {
    border-color: var(--orange) !important;
    padding: 5px 15px 5px 37px !important;
  }
}
</style>
